import { Component, Input } from '@angular/core';
import { FormControl } from '@angular/forms';
import { FileInputFormat } from '@shared/file-input/file-input-format.model';
import { FormHelpers } from '@shared/form-helpers';
import { TooltipHelpers } from '@shared/tooltip-helpers';
import { zbpIconNames } from '@shared/zbp-icon/zbp-icon-names';
import { firstValueFrom, Observable, of, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import MAX_STUDENTS_TOOLTIP = TooltipHelpers.MAX_STUDENTS_TOOLTIP;

@Component({
  selector: 'zbp-file-input[uniqueId][fileControl]',
  templateUrl: './file-input.component.html',
  styleUrls: ['./file-input.component.scss'],
  standalone: false
})
export class FileInputComponent {

  /** this is a warning that the parent component has not provided an upload handler */
  static UPLOAD_HANDLER_MISSING_MESSAGE = 'Upload Handler Not Connected!';

  protected readonly zbpIconNames = zbpIconNames;
  protected readonly Boolean = Boolean;
  MAX_STUDENTS_TOOLTIP = MAX_STUDENTS_TOOLTIP;

  /** REQUIRED or the component will fail to work in your template. */
  @Input() uniqueId: string;

  /** REQUIRED or the component will fail to work in your template. */
  @Input() fileControl: FormControl<File>;

  /** Optional, used for validation, therefore a default is provided */
  @Input() fileNameControl?: FormControl<string> = new FormControl(null);

  /**
   * TODO this should default to true once drag and drop is implemented
   * This only exists currently to add the styles.
   */
  @Input() dragAndDropEnabled: boolean = false;

  @Input() supportedFormats: Array<FileInputFormat> = [];

  /**
   * Takes a File inside a FormControl, should perform an upload,
   * then return any errors as an array of strings.
   */
  @Input() uploadButtonHandler:
    (formControl: FormControl<File>) => Observable<Array<string>> = () => throwError(
      () => new Error(FileInputComponent.UPLOAD_HANDLER_MISSING_MESSAGE)
    );

  /**
   * List of instructions to be displayed under success message.
   */
  @Input() successInstructions: Array<string> = [];

  /**
   * Whether or not the input should be disabled.
   */
  @Input() disabled: boolean = false;

  uploadInProgress: boolean = false;
  uploadSuccess: boolean = false;
  uploadErrors: Array<string> = [];

  constructor() {
  }

  hasError(): boolean {
    return this.fileControl.invalid
      || this.fileNameControl?.invalid
      || this.uploadErrors.length > 0;
  }

  getSupportedExtensions(): string {
    return this.supportedFormats
      .map(format => format.extension)
      .join(', ');
  }

  getAllErrors(): Array<string> {
    return FormHelpers.getAllFormErrors(this.fileNameControl)
      .concat(FormHelpers.getAllFormErrors(this.fileControl))
      .concat(this.uploadErrors);
  }

  onFileInputChange(event: any): void {
    const target = event.target as HTMLInputElement;
    if (target.files && target.files.length > 0) {
      this.fileControl.patchValue(target.files[0]);
    }
  }

  onFileXButtonClick(): void {
    this.fileControl.reset();
    this.fileNameControl?.reset();
    this.uploadErrors = [];
  }

  async onUploadButtonClick(): Promise<void> {
    this.uploadInProgress = true;
    this.uploadErrors = [];
    this.uploadSuccess = false;
    this.uploadErrors = await firstValueFrom(
      this.uploadButtonHandler(this.fileControl).pipe(catchError(
        (err: Error) => of([`${err.name}: ${err.message}`])
      ))
    );
    this.uploadSuccess = this.uploadErrors.length === 0;
    this.uploadInProgress = false;
  }

}
